import React from "react";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { formatDateReadable } from "../../utilities/helpers";


function formatConductedAt(isoString) {
  if (!isoString) return "";
  try {
    return formatDateReadable(isoString.slice(0, 10));
  } catch {
    return isoString;
  }
}


function formatValue(field, value) {
  if (value == null) return "—";
  if (field.field_type === "text" && typeof value.text === "string") {
    return value.text;
  }
  if (field.field_type === "scale" && typeof value.number === "number") {
    const max = field?.config?.max;
    return typeof max === "number" ? `${value.number} / ${max}` : String(value.number);
  }
  if (field.field_type === "yes_no" && typeof value.bool === "boolean") {
    return value.bool ? "Yes" : "No";
  }
  if (field.field_type === "single_select" && typeof value.option === "string") {
    return value.option;
  }
  return "—";
}


function CheckInsHistoryCard(props) {
  const ci = props.checkIn;
  const isShared = ci.is_shared_with_employee;
  const sectionsSummary = ci.sections_summary || [];
  const answered = ci.answered_count ?? 0;
  const total = ci.total_count ?? 0;
  const hasContent = sectionsSummary.length > 0;
  const [expanded, setExpanded] = React.useState(false);

  // Tint the completion ratio based on progress.
  let countClasses;
  if (total === 0) {
    countClasses = "bg-slate-50 border-slate-200 text-slate-500";
  } else if (answered === 0) {
    countClasses = "bg-slate-50 border-slate-300 text-slate-600";
  } else if (answered < total) {
    countClasses = "bg-amber-50 border-amber-300 text-amber-700";
  } else {
    countClasses = "bg-emerald-50 border-emerald-300 text-emerald-700";
  }

  return (
    <div>
      {/* Header row — whole row (full p-6 area) is clickable to toggle the
          expander, with a slate hover so the affordance is obvious. Edit
          button stops propagation so it doesn't also toggle. */}
      <div
        className={`flex items-start gap-4 p-6 ${
          hasContent
            ? "cursor-pointer hover:bg-slate-50 transition-colors"
            : ""
        }`}
        role={hasContent ? "button" : undefined}
        tabIndex={hasContent ? 0 : undefined}
        aria-expanded={hasContent ? expanded : undefined}
        onClick={() => {
          if (hasContent) setExpanded((v) => !v);
        }}
        onKeyDown={(e) => {
          if (!hasContent) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setExpanded((v) => !v);
          }
        }}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-900">
              {formatConductedAt(ci.conducted_at)}
            </span>
            {ci.author_stm?.full_name && (
              <span className="text-sm text-gray-500">
                · {ci.author_stm.full_name}
              </span>
            )}

            <span
              className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-md border ${countClasses}`}
            >
              {answered} of {total} answered
            </span>

            {isShared ? (
              <span className="inline-flex items-center gap-1 bg-violet-50 border border-violet-300 text-violet-700 text-xs font-medium px-2 py-0.5 rounded-md">
                <VisibilityIcon style={{ fontSize: "14px" }} />
                Shared
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 bg-slate-50 border border-slate-300 text-slate-600 text-xs font-medium px-2 py-0.5 rounded-md">
                <VisibilityOffIcon style={{ fontSize: "14px" }} />
                Private
              </span>
            )}
          </div>
        </div>

        <div className="shrink-0 flex items-center gap-3">
          {hasContent && (
            <span
              className="text-slate-500 flex items-center"
              aria-hidden="true"
            >
              {expanded ? (
                <KeyboardArrowUpIcon style={{ fontSize: "22px" }} />
              ) : (
                <KeyboardArrowDownIcon style={{ fontSize: "22px" }} />
              )}
            </span>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              props.onViewFull && props.onViewFull();
            }}
            className="text-sm font-medium text-violet-700 hover:text-violet-800"
          >
            Edit
          </button>
        </div>
      </div>

      {/* Expanded body — padded inside its own px-6 so the dividing line
          between header and body stays inset (matches the pre-restructure
          look) instead of bleeding to the card's outer edges. */}
      {expanded && hasContent && (
        <div className="px-6 pb-6">
          <div className="pt-4 border-t border-gray-100 space-y-4">
            {sectionsSummary.map((section) => (
              <div key={`${ci.uuid}-${section.title}`}>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                  {section.title}
                </h4>
                <div className="space-y-2">
                  {section.answered_fields.map((field) => (
                    <div key={field.uuid}>
                      <p className="text-xs font-medium text-slate-600">
                        {field.label}
                      </p>
                      <p className="text-sm text-slate-800 whitespace-pre-wrap">
                        {formatValue(field, field.value)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default CheckInsHistoryCard;
